import { getTeamsWithProjects } from "frontend/api";
import { useAtom } from "jotai";
import { Team, TeamWithProjects } from "shared/datamodel/schemas/team";
import { Project } from "shared/datamodel/schemas/project";
import { teamsWithProjectsAtom } from "state-atoms";
import { pick } from "rambda";

// Module-level cache for project-to-team relationships
const projectsParentCache = new Map<string, TeamWithProjects>();

export function useTeamsWithProjects() {
  const [teamsWithProjects, setTeamsWithProjects] = useAtom(teamsWithProjectsAtom);
  const addTeam = (team: Pick<Team, "documentId" | "name" | "permission" | "isOwner" | "urlId">) => {
    setTeamsWithProjects((previous) => [...(previous ?? []), { ...team, projects: [] }]);
  };

  const updateTeam = (teamId: string, updates: Partial<Team>) => {
    updates = pick(["name", "permission", "isOwner"], updates);
    setTeamsWithProjects(
      (previous) =>
        previous?.map((team) =>
          team.documentId === teamId ? { ...team, ...updates, projects: team.projects ?? [] } : team
        ) ?? []
    );
  };

  const getTeamsProjects = (teamId: string) => {
    return teamsWithProjects?.find((team) => team.documentId === teamId)?.projects;
  };

  const deleteTeam = (teamId: string) => {
    setTeamsWithProjects((previous) => previous?.filter((team) => team.documentId !== teamId) ?? []);
  };

  const addProject = (
    teamId: string,
    project: Pick<Project, "documentId" | "name" | "permission" | "isOwner" | "urlId">
  ) => {
    setTeamsWithProjects(
      (previous) =>
        previous?.map((team) =>
          team.documentId === teamId ? { ...team, projects: [...team.projects, project] } : team
        ) ?? []
    );
  };

  const updateProject = (
    teamId: string,
    projectId: string,
    updates: Partial<Pick<Project, "name" | "permission" | "isOwner" | "urlId">>
  ) => {
    setTeamsWithProjects(
      (previous) =>
        previous?.map((team) =>
          team.documentId === teamId
            ? {
                ...team,
                projects: team.projects?.map((project) =>
                  project.documentId === projectId ? { ...project, ...updates } : project
                ),
              }
            : team
        ) ?? []
    );
  };

  const deleteProject = (teamId: string, projectId: string) => {
    setTeamsWithProjects(
      (previous) =>
        previous?.map((team) =>
          team.documentId === teamId
            ? {
                ...team,
                projects: team.projects?.filter((project) => project.documentId !== projectId),
              }
            : team
        ) ?? []
    );
  };

  const fetchTeamsWithProjects = async (): Promise<TeamWithProjects[]> => {
    const teamsWithProjects = await getTeamsWithProjects();
    setTeamsWithProjects(teamsWithProjects);
    return teamsWithProjects;
  };

  const getProjectsParent = (projectId: string) => {
    // Check cache first
    if (projectsParentCache.has(projectId)) {
      return projectsParentCache.get(projectId);
    }

    // Find team in current teamsWithProjects
    const team = teamsWithProjects?.find((team) => team.projects?.some((project) => project.documentId === projectId));
    if (team) {
      // Update cache synchronously
      projectsParentCache.set(projectId, team);
      return team;
    }
  };

  return {
    teamsWithProjects,
    fetchTeamsWithProjects,
    addTeam,
    updateTeam,
    deleteTeam,
    addProject,
    updateProject,
    deleteProject,
    getTeamsProjects,
    getProjectsParent,
  };
}
